// ============================================
// CAMPANAS.JS — Módulo de gestión de campañas
// Vista A: lista de campañas
// Vista B: detalle con 3 pestañas (Leads, QR, Formulario)
// ============================================

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { STORAGE_KEYS, storage } from './utils/storage';
import { API_BASE } from './config/api';

const API = API_BASE;   // direccion del backend: config/api.js (fuente unica)
const URL_FORMULARIO_BASE = 'https://beep-os-production-5a7b.up.railway.app/expo-leads.html';

const PP = "'Poppins', sans-serif";
const C = {
  principal: '#5c9adb',
  oscuro:    '#2f5f9e',
  acento:    '#dcde74',
  azulQr:    '#5e99d2',
  azulClaro: '#8fb8e8',
  fondo:     '#f3f4f4',
  gris:      '#4e5357',
  grisClaro: '#8a8f95',
  verde:     '#1D9E75',
  rojo:      '#e05c5c',
};

function authHeaders() {
  const token = localStorage.getItem('beep_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
  };
}

function fechaCorta(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return '—'; }
}

function fechaHora(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return '—'; }
}

// ============================================
// COMPONENTE PRINCIPAL
// ============================================
export default function Campanas() {
  const [vista, setVista] = useState('lista'); // 'lista' | 'detalle'
  const [campanaActiva, setCampanaActiva] = useState(null);
  const [ancho, setAncho] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const navigate = useNavigate();

  useEffect(() => {
    const onResize = () => setAncho(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const esMovil = ancho < 768;

  if (vista === 'detalle' && campanaActiva) {
    return (
      <DetalleCampana
        campanaId={campanaActiva.id}
        esMovil={esMovil}
        onVolver={() => { setVista('lista'); setCampanaActiva(null); }}
      />
    );
  }

  return (
    <ListaCampanas
      esMovil={esMovil}
      onAbrir={(c) => { setCampanaActiva(c); setVista('detalle'); }}
      // FIX-CAMPANAS-BACK: limpiar tab activo del Dashboard antes de navegar
      // para evitar loop infinito (Dashboard re-lee localStorage y vuelve aqui).
      // Patron espejo de App.js:191-193 para Expo Alianza.
      onVolverDashboard={() => {
        storage.remove(STORAGE_KEYS.DASHBOARD_ACTIVE_TAB);
        navigate('/');
      }}
    />
  );
}

// ============================================
// VISTA A — LISTA DE CAMPAÑAS
// ============================================
function ListaCampanas({ esMovil, onAbrir, onVolverDashboard }) {
  const [campanas, setCampanas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [modalAbierto, setModalAbierto] = useState(false);
  const [form, setForm] = useState({ nombre: '', descripcion: '', fecha_inicio: '', fecha_fin: '' });
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError('');
    try {
      const r = await fetch(`${API}/api/campanas`, { headers: authHeaders() });
      const d = await r.json();
      if (d.ok) setCampanas(d.campanas || []);
      else setError(d.error || 'Error cargando campañas');
    } catch {
      setError('Error de red');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const crear = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) return;
    setGuardando(true);
    try {
      const r = await fetch(`${API}/api/campanas`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(form),
      });
      const d = await r.json();
      if (d.ok) {
        setForm({ nombre: '', descripcion: '', fecha_inicio: '', fecha_fin: '' });
        setModalAbierto(false);
        cargar();
      }
    } catch {
      setError('Error al crear campaña');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div style={{ fontFamily: PP, background: C.fondo, minHeight: '100vh', padding: esMovil ? 12 : 24 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={onVolverDashboard} style={{
            background: '#fff', border: `1px solid ${C.grisClaro}33`, borderRadius: 10,
            padding: '8px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
            color: C.gris, fontFamily: PP,
          }}>← Dashboard</button>
          <div>
            <div style={{ fontSize: esMovil ? 22 : 26, fontWeight: 800, color: C.gris }}>Campañas</div>
            <div style={{ fontSize: 12, color: C.grisClaro, marginTop: 2 }}>
              Gestiona tus campañas y captación de leads
            </div>
          </div>
        </div>
        <button onClick={() => setModalAbierto(true)} style={{
          background: C.acento, color: C.gris, border: 'none', borderRadius: 10,
          padding: '10px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: PP,
        }}>+ Nueva campaña</button>
      </div>

      {/* Cards */}
      {cargando ? (
        <div style={{ background: '#fff', borderRadius: 14, padding: 40, textAlign: 'center', color: C.grisClaro, fontSize: 13 }}>
          Cargando campañas…
        </div>
      ) : error ? (
        <div style={{ background: '#fff', borderRadius: 14, padding: 20, textAlign: 'center' }}>
          <div style={{ color: C.rojo, fontSize: 13, marginBottom: 8 }}>{error}</div>
          <button onClick={cargar} style={{
            background: C.principal, color: '#fff', border: 'none', borderRadius: 8,
            padding: '8px 16px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: PP,
          }}>Reintentar</button>
        </div>
      ) : campanas.length === 0 ? (
        <div style={{ background: '#fff', borderRadius: 14, padding: 40, textAlign: 'center', color: C.grisClaro, fontSize: 13 }}>
          Aún no hay campañas. Crea la primera con el botón <strong>"+ Nueva campaña"</strong>.
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: esMovil ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 14,
        }}>
          {campanas.map(c => {
            const colorEstado = c.estado === 'activa' ? C.verde : C.grisClaro;
            const bgEstado = c.estado === 'activa' ? '#e8f5ee' : '#f0f0f0';
            return (
              <div key={c.id} onClick={() => onAbrir(c)} style={{
                background: '#fff', borderRadius: 14, padding: 18, cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                borderLeft: `4px solid ${colorEstado}`,
                transition: 'transform 0.15s, box-shadow 0.15s',
              }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.1)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)'; }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, marginBottom: 10 }}>
                  <div style={{ fontSize: 16, fontWeight: 800, color: C.oscuro, lineHeight: 1.2 }}>
                    {c.nombre}
                  </div>
                  <span style={{
                    background: bgEstado, color: colorEstado,
                    padding: '3px 10px', borderRadius: 999, fontSize: 10,
                    fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.4,
                    flexShrink: 0,
                  }}>{c.estado}</span>
                </div>
                {c.descripcion && (
                  <div style={{ fontSize: 12, color: C.grisClaro, marginBottom: 12, lineHeight: 1.4 }}>
                    {c.descripcion}
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 11, color: C.grisClaro, marginBottom: 10 }}>
                  <span>📅 {fechaCorta(c.fecha_inicio)}</span>
                  <span>→</span>
                  <span>{fechaCorta(c.fecha_fin)}</span>
                </div>
                <div style={{
                  background: C.fondo, borderRadius: 8, padding: '10px 12px',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                  <span style={{ fontSize: 11, color: C.grisClaro, fontWeight: 600 }}>LEADS CAPTURADOS</span>
                  <span style={{ fontSize: 18, fontWeight: 800, color: C.principal }}>
                    {c.total_leads ?? 0}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal nueva campaña */}
      {modalAbierto && (
        <>
          <div onClick={() => setModalAbierto(false)} style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200,
          }} />
          <form onSubmit={crear} style={{
            position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            background: '#fff', borderRadius: 16, padding: 24, width: '90%', maxWidth: 460,
            zIndex: 201, fontFamily: PP, boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
          }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: C.gris, marginBottom: 16 }}>
              Nueva campaña
            </div>

            <Label>Nombre *</Label>
            <Input value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} required placeholder="Ej: Black Friday 2026" />

            <Label>Descripción</Label>
            <Textarea value={form.descripcion} onChange={e => setForm({ ...form, descripcion: e.target.value })} rows={2} placeholder="Breve descripción..." />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <Label>Fecha inicio</Label>
                <Input type="date" value={form.fecha_inicio} onChange={e => setForm({ ...form, fecha_inicio: e.target.value })} />
              </div>
              <div>
                <Label>Fecha fin</Label>
                <Input type="date" value={form.fecha_fin} onChange={e => setForm({ ...form, fecha_fin: e.target.value })} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button type="button" onClick={() => setModalAbierto(false)} style={{
                flex: 1, background: 'transparent', border: `1px solid ${C.grisClaro}66`,
                borderRadius: 10, padding: '12px', fontSize: 13, fontWeight: 600,
                cursor: 'pointer', color: C.gris, fontFamily: PP,
              }}>Cancelar</button>
              <button type="submit" disabled={guardando || !form.nombre.trim()} style={{
                flex: 1, background: guardando ? '#ccc' : C.acento, color: C.gris, border: 'none',
                borderRadius: 10, padding: '12px', fontSize: 13, fontWeight: 700,
                cursor: guardando ? 'not-allowed' : 'pointer', fontFamily: PP,
              }}>{guardando ? 'Guardando…' : 'Crear campaña'}</button>
            </div>
          </form>
        </>
      )}
    </div>
  );
}

// ============================================
// VISTA B — DETALLE DE CAMPAÑA
// ============================================
// Display config para los 5 agentes IA
const AGENTES_DISPLAY = [
  { id: 'luna',   nombre: 'Luna',   tipo: 'estrategia',   tituloTipo: 'Estrategia general', emoji: '✨', color: '#94b803' },
  { id: 'stella', nombre: 'Stella', tipo: 'contenido',    tituloTipo: 'Ideas de contenido', emoji: '⭐', color: '#E1306C' },
  { id: 'spark',  nombre: 'Spark',  tipo: 'seguimiento',  tituloTipo: 'Seguimiento de leads', emoji: '⚡', color: '#FF6B35' },
  { id: 'iris',   nombre: 'Iris',   tipo: 'visual',       tituloTipo: 'Concepts visuales', emoji: '🌈', color: '#9B59B6' },
  { id: 'dato',   nombre: 'Dato',   tipo: 'kpis',         tituloTipo: 'KPIs y métricas', emoji: '📊', color: '#185FA5' },
];

function DetalleCampana({ campanaId, esMovil, onVolver }) {
  const [campana, setCampana] = useState(null);
  const [leads, setLeads] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [pestana, setPestana] = useState('leads'); // 'leads' | 'qr' | 'form' | 'ia'
  const [enFullscreen, setEnFullscreen] = useState(false);
  const [soportaFs] = useState(typeof document !== 'undefined' && !!document.documentElement.requestFullscreen);
  const [copiado, setCopiado] = useState(false);
  // Análisis IA
  const [iaData, setIaData] = useState([]);
  const [cargandoIA, setCargandoIA] = useState(false);
  const [regenerandoIA, setRegenerandoIA] = useState(false);

  const urlFormulario = `${URL_FORMULARIO_BASE}?campana=${campanaId}`;

  const cargar = useCallback(async () => {
    setCargando(true);
    setError('');
    try {
      const r = await fetch(`${API}/api/campanas/${campanaId}`, { headers: authHeaders() });
      const d = await r.json();
      if (d.ok) {
        setCampana(d.campana);
        setLeads(d.leads || []);
      } else {
        setError(d.error || 'Error cargando campaña');
      }
    } catch {
      setError('Error de red');
    } finally {
      setCargando(false);
    }
  }, [campanaId]);

  useEffect(() => { cargar(); }, [cargar]);

  // Auto-refresh cada 30s en pestaña Leads
  useEffect(() => {
    if (pestana !== 'leads') return;
    const id = setInterval(cargar, 30000);
    return () => clearInterval(id);
  }, [pestana, cargar]);

  // Fullscreen state listener
  useEffect(() => {
    const onChange = () => setEnFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggleFs = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else document.documentElement.requestFullscreen();
  };

  // ============ IA helpers ============
  const cargarIA = useCallback(async () => {
    setCargandoIA(true);
    try {
      const r = await fetch(`${API}/api/campanas/${campanaId}/ia`, { headers: authHeaders() });
      const d = await r.json();
      if (d.ok) setIaData(d.ia || []);
    } catch {}
    finally { setCargandoIA(false); }
  }, [campanaId]);

  const regenerarIA = async () => {
    if (!window.confirm('¿Regenerar el análisis IA? Esto borrará el actual y volverá a llamar a los 5 agentes.')) return;
    setRegenerandoIA(true);
    setIaData([]);
    try {
      const r = await fetch(`${API}/api/campanas/${campanaId}/regenerar-ia`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const d = await r.json();
      if (!d.ok) alert(d.error || 'Error regenerando');
    } catch {
      alert('Error de red');
    } finally {
      setRegenerandoIA(false);
      setTimeout(cargarIA, 1500);
    }
  };

  // Cargar IA al entrar a la pestaña
  useEffect(() => {
    if (pestana === 'ia') cargarIA();
  }, [pestana, cargarIA]);

  // Polling cada 5s mientras la IA esté incompleta (menos de 5 registros) y la pestaña esté activa
  useEffect(() => {
    if (pestana !== 'ia') return;
    if (iaData.length >= 5) return;
    const id = setInterval(cargarIA, 5000);
    return () => clearInterval(id);
  }, [pestana, iaData.length, cargarIA]);

  const copiarUrl = async () => {
    try {
      await navigator.clipboard.writeText(urlFormulario);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1800);
    } catch {
      // fallback
      const ta = document.createElement('textarea');
      ta.value = urlFormulario;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); setCopiado(true); setTimeout(() => setCopiado(false), 1800); } catch {}
      document.body.removeChild(ta);
    }
  };

  if (cargando && !campana) {
    return (
      <div style={{ fontFamily: PP, background: C.fondo, minHeight: '100vh', padding: 24, textAlign: 'center', color: C.grisClaro }}>
        Cargando campaña…
      </div>
    );
  }
  if (error || !campana) {
    return (
      <div style={{ fontFamily: PP, background: C.fondo, minHeight: '100vh', padding: 24 }}>
        <button onClick={onVolver} style={{ background: '#fff', border: `1px solid ${C.grisClaro}33`, borderRadius: 10, padding: '8px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: C.gris, fontFamily: PP, marginBottom: 14 }}>← Volver</button>
        <div style={{ background: '#fff', borderRadius: 14, padding: 20, color: C.rojo, fontSize: 13 }}>{error || 'Campaña no encontrada'}</div>
      </div>
    );
  }

  const pestanas = [
    { id: 'leads', label: '📋 Leads', icon: '📋' },
    { id: 'qr',    label: '📱 Código QR', icon: '📱' },
    { id: 'form',  label: '🔗 Formulario', icon: '🔗' },
    { id: 'ia',    label: '🤖 Equipo IA', icon: '🤖' },
  ];

  return (
    <div style={{ fontFamily: PP, background: C.fondo, minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ background: '#fff', padding: esMovil ? '14px 16px' : '16px 24px', borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <button onClick={onVolver} style={{
          background: C.fondo, border: 'none', borderRadius: 10,
          padding: '8px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
          color: C.gris, fontFamily: PP,
        }}>← Volver</button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: esMovil ? 18 : 22, fontWeight: 800, color: C.oscuro, lineHeight: 1.2 }}>
            {campana.nombre}
          </div>
          <div style={{ fontSize: 11, color: C.grisClaro, marginTop: 3 }}>
            {fechaCorta(campana.fecha_inicio)} → {fechaCorta(campana.fecha_fin)} ·
            <span style={{ color: campana.estado === 'activa' ? C.verde : C.grisClaro, fontWeight: 700, marginLeft: 4 }}>
              {campana.estado.toUpperCase()}
            </span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ background: '#fff', display: 'flex', borderBottom: '1px solid #eee', overflowX: 'auto' }}>
        {pestanas.map(p => {
          const activa = pestana === p.id;
          return (
            <button key={p.id} onClick={() => setPestana(p.id)} style={{
              padding: '14px 20px', background: 'transparent', border: 'none',
              borderBottom: `3px solid ${activa ? C.principal : 'transparent'}`,
              fontSize: 13, fontWeight: activa ? 700 : 500,
              color: activa ? C.oscuro : C.grisClaro,
              cursor: 'pointer', fontFamily: PP, whiteSpace: 'nowrap',
              transition: 'all 0.15s',
            }}>{p.label}</button>
          );
        })}
      </div>

      {/* Contenido por pestaña */}
      {pestana === 'leads' && (
        <div style={{ padding: esMovil ? 12 : 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.gris }}>
              {leads.length} {leads.length === 1 ? 'lead capturado' : 'leads capturados'}
            </div>
            <button onClick={cargar} disabled={cargando} style={{
              background: '#fff', border: `1px solid ${C.principal}55`,
              borderRadius: 10, padding: '8px 14px', fontSize: 12, fontWeight: 700,
              cursor: cargando ? 'not-allowed' : 'pointer', color: C.principal, fontFamily: PP,
            }}>{cargando ? 'Actualizando…' : '↻ Actualizar'}</button>
          </div>

          {leads.length === 0 ? (
            <div style={{ background: '#fff', borderRadius: 14, padding: 40, textAlign: 'center', color: C.grisClaro, fontSize: 13 }}>
              <div style={{ fontSize: 36, marginBottom: 10, opacity: 0.4 }}>📭</div>
              Aún no hay leads en esta campaña
              <div style={{ fontSize: 11, marginTop: 6 }}>
                Comparte el código QR o el enlace del formulario para empezar a capturar
              </div>
            </div>
          ) : (
            <div style={{ background: '#fff', borderRadius: 14, overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
                <thead>
                  <tr style={{ background: C.fondo }}>
                    <Th>Nombre</Th>
                    <Th>Teléfono</Th>
                    <Th>Servicio</Th>
                    <Th>Tipo de lugar</Th>
                    <Th>Fuente</Th>
                    <Th>Fecha</Th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l, i) => (
                    <tr key={l.id} style={{ borderBottom: '1px solid #f0f0f0', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <Td bold>{l.nombre || '—'}</Td>
                      <Td>{l.telefono || '—'}</Td>
                      <Td>{l.servicio || '—'}</Td>
                      <Td>{l.tipo_lugar || '—'}</Td>
                      <Td>{l.fuente || '—'}</Td>
                      <Td small>{fechaHora(l.creado_en)}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {pestana === 'qr' && (
        <div style={{
          background: C.azulQr,
          minHeight: 'calc(100vh - 130px)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: esMovil ? 24 : 36, padding: esMovil ? '24px 16px' : '40px 30px',
          position: 'relative', overflow: 'hidden',
        }}>
          {/* Botón fullscreen */}
          {soportaFs && (
            <button onClick={toggleFs} title={enFullscreen ? 'Salir' : 'Pantalla completa'} style={{
              position: 'absolute', top: 12, right: 12, zIndex: 10,
              background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: 8, padding: '6px 10px', color: '#fff', fontSize: 16,
              cursor: 'pointer', fontFamily: PP, backdropFilter: 'blur(4px)',
            }}>{enFullscreen ? '✕' : '⛶'}</button>
          )}

          <style>{`
            @keyframes camp-spin1 { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
            @keyframes camp-spin2 { from { transform: rotate(360deg); } to { transform: rotate(0deg); } }
            @keyframes camp-pulse {
              0%, 100% { transform: scale(1); }
              50% { transform: scale(1.015); }
            }
          `}</style>

          {/* Logo con anillos giratorios (igual ExpoKiosko) */}
          <div style={{ position: 'relative', width: 90, height: 90, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{
              position: 'absolute', width: 90, height: 90,
              border: `2px solid ${C.acento}66`, borderRadius: '50%',
              animation: 'camp-spin1 10s linear infinite',
            }}>
              <div style={{ position: 'absolute', top: -4, left: 34, width: 8, height: 8, borderRadius: '50%', background: C.acento }} />
            </div>
            <div style={{
              position: 'absolute', width: 74, height: 74,
              border: `1.5px solid ${C.azulClaro}55`, borderRadius: '50%',
              animation: 'camp-spin2 14s linear infinite',
            }}>
              <div style={{ position: 'absolute', bottom: -3, right: 10, width: 6, height: 6, borderRadius: '50%', background: C.azulClaro }} />
            </div>
            <img src="/icono azul beep.png" alt="Beep" style={{ width: 56, height: 56, objectFit: 'contain', position: 'relative', zIndex: 2 }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          </div>

          {/* QR */}
          <div style={{
            background: '#fff', padding: 24, borderRadius: 20,
            boxShadow: '0 8px 28px rgba(0,0,0,0.18)',
            animation: 'camp-pulse 2.4s ease-in-out infinite',
          }}>
            <QRCodeSVG
              value={urlFormulario}
              size={esMovil ? 220 : 300}
              level="H"
              bgColor="#ffffff"
              fgColor={C.azulQr}
              includeMargin={false}
            />
          </div>

          <div style={{ textAlign: 'center' }}>
            <div style={{
              fontSize: esMovil ? 18 : 24, fontWeight: 800, color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 4,
            }}>
              <span style={{ fontSize: esMovil ? 22 : 28 }}>📱</span>
              Escanea para registrarte
            </div>
            <div style={{
              fontSize: 11, color: C.acento, letterSpacing: '0.2em',
              textTransform: 'uppercase', fontWeight: 700,
            }}>{campana.nombre}</div>
          </div>
        </div>
      )}

      {pestana === 'form' && (
        <div style={{ padding: esMovil ? 16 : 32, maxWidth: 640, margin: '0 auto' }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 24, marginBottom: 14 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: C.gris, marginBottom: 8 }}>
              🔗 Enlace del formulario
            </div>
            <div style={{ fontSize: 13, color: C.grisClaro, lineHeight: 1.5, marginBottom: 16 }}>
              Comparte este enlace o el código QR con tus prospectos. Cada lead capturado quedará vinculado a esta campaña.
            </div>

            <div style={{
              background: C.fondo, borderRadius: 10, padding: '12px 14px',
              fontFamily: 'monospace', fontSize: 12, color: C.oscuro,
              wordBreak: 'break-all', marginBottom: 10, userSelect: 'all',
            }}>{urlFormulario}</div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button onClick={copiarUrl} style={{
                flex: 1, minWidth: 140, background: copiado ? C.verde : C.principal, color: '#fff',
                border: 'none', borderRadius: 10, padding: '12px',
                fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: PP,
                transition: 'background 0.2s',
              }}>{copiado ? '✓ Copiado' : '📋 Copiar enlace'}</button>
              <a href={urlFormulario} target="_blank" rel="noopener noreferrer" style={{
                flex: 1, minWidth: 140, background: C.acento, color: C.gris,
                border: 'none', borderRadius: 10, padding: '12px',
                fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: PP,
                textDecoration: 'none', textAlign: 'center', display: 'block',
                boxSizing: 'border-box',
              }}>↗ Abrir en nueva pestaña</a>
            </div>
          </div>

          <div style={{ background: '#fff', borderRadius: 14, padding: 20, fontSize: 12, color: C.grisClaro, lineHeight: 1.6 }}>
            <div style={{ fontWeight: 700, color: C.gris, marginBottom: 6 }}>💡 Cómo usar</div>
            • Cada vez que un prospecto escanee el QR o use el enlace, su información llegará directo al panel de leads de esta campaña.<br/>
            • Puedes refrescar la pestaña <strong>Leads</strong> para ver los nuevos registros (se actualiza automáticamente cada 30s).<br/>
            • Para usar el QR en una tableta como kiosko, abre la pestaña <strong>Código QR</strong> y presiona ⛶ para pantalla completa.
          </div>
        </div>
      )}

      {pestana === 'ia' && (
        <div style={{ padding: esMovil ? 12 : 24, maxWidth: 920, margin: '0 auto' }}>
          <style>{`
            @keyframes ia-pulse { 0%,100%{opacity:1;transform:scale(1);} 50%{opacity:0.6;transform:scale(0.95);} }
            @keyframes ia-bounce { 0%,80%,100%{transform:translateY(0);} 40%{transform:translateY(-6px);} }
          `}</style>

          {/* Header de la pestaña */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, color: C.gris }}>
                🤖 Análisis del equipo IA
              </div>
              <div style={{ fontSize: 12, color: C.grisClaro, marginTop: 2 }}>
                {iaData.length === 0 && cargandoIA
                  ? 'Cargando...'
                  : `${iaData.length} de 5 análisis listos`}
              </div>
            </div>
            {iaData.length > 0 && (
              <button
                onClick={regenerarIA}
                disabled={regenerandoIA}
                style={{
                  background: '#fff', border: `1px solid ${C.principal}55`,
                  borderRadius: 10, padding: '8px 14px', fontSize: 12, fontWeight: 700,
                  cursor: regenerandoIA ? 'not-allowed' : 'pointer', color: C.principal, fontFamily: PP,
                  opacity: regenerandoIA ? 0.6 : 1,
                }}
              >{regenerandoIA ? 'Regenerando…' : '↻ Regenerar análisis'}</button>
            )}
          </div>

          {/* Estado vacío: Luna está coordinando */}
          {iaData.length === 0 && !cargandoIA && (
            <div style={{ background: '#fff', borderRadius: 14, padding: 40, textAlign: 'center' }}>
              <div style={{ fontSize: 48, marginBottom: 12, animation: 'ia-pulse 1.5s ease-in-out infinite' }}>✨</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: C.oscuro, marginBottom: 6 }}>
                Luna está coordinando al equipo...
              </div>
              <div style={{ fontSize: 12, color: C.grisClaro, marginBottom: 14 }}>
                Stella, Spark, Iris y Dato están preparando sus análisis.
              </div>
              <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                {[0, 1, 2].map(i => (
                  <span key={i} style={{
                    width: 8, height: 8, borderRadius: '50%', background: C.principal,
                    animation: `ia-bounce 1.4s ease-in-out infinite`, animationDelay: `${i * 0.15}s`,
                  }} />
                ))}
              </div>
            </div>
          )}

          {/* Tarjetas de los 5 agentes */}
          {iaData.length > 0 && (
            <div style={{ display: 'grid', gap: 14 }}>
              {AGENTES_DISPLAY.map(a => {
                const registro = iaData.find(x => x.agente === a.id);
                const tieneContenido = !!(registro && registro.contenido && registro.contenido !== 'Sin respuesta del agente');
                return (
                  <div key={a.id} style={{
                    background: '#fff', borderRadius: 14, padding: 18,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    borderLeft: `4px solid ${a.color}`,
                  }}>
                    {/* Header de la tarjeta */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                      <div style={{
                        width: 44, height: 44, borderRadius: '50%',
                        background: a.color, color: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 22, flexShrink: 0,
                      }}>{a.emoji}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 15, fontWeight: 800, color: a.color }}>
                          {a.nombre}
                        </div>
                        <div style={{ fontSize: 11, color: C.grisClaro, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                          {a.tituloTipo}
                        </div>
                      </div>
                      {tieneContenido && (
                        <span style={{
                          background: '#e8f5ee', color: C.verde,
                          padding: '3px 10px', borderRadius: 999,
                          fontSize: 10, fontWeight: 800,
                        }}>✓ Listo</span>
                      )}
                    </div>

                    {/* Contenido */}
                    {tieneContenido ? (
                      <div style={{
                        fontSize: 13, color: C.gris, lineHeight: 1.6,
                        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                        background: C.fondo, borderRadius: 10, padding: '12px 14px',
                      }}>
                        {registro.contenido}
                      </div>
                    ) : (
                      <div style={{
                        fontSize: 13, color: C.grisClaro,
                        background: C.fondo, borderRadius: 10, padding: '14px',
                        display: 'flex', alignItems: 'center', gap: 8,
                        fontStyle: 'italic',
                      }}>
                        <span style={{ display: 'inline-flex', gap: 3 }}>
                          {[0, 1, 2].map(i => (
                            <span key={i} style={{
                              width: 5, height: 5, borderRadius: '50%', background: a.color,
                              animation: 'ia-bounce 1.4s ease-in-out infinite',
                              animationDelay: `${i * 0.15}s`,
                            }} />
                          ))}
                        </span>
                        Generando...
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================
// HELPERS UI
// ============================================
function Label({ children }) {
  return <div style={{ fontSize: 11, fontWeight: 700, color: C.grisClaro, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4, marginTop: 12 }}>{children}</div>;
}

function Input(props) {
  return <input {...props} style={{
    width: '100%', padding: '10px 12px', borderRadius: 8,
    border: `1px solid ${C.grisClaro}44`, fontSize: 13, fontFamily: PP,
    outline: 'none', boxSizing: 'border-box', color: C.gris, background: '#fff',
  }} />;
}

function Textarea(props) {
  return <textarea {...props} style={{
    width: '100%', padding: '10px 12px', borderRadius: 8,
    border: `1px solid ${C.grisClaro}44`, fontSize: 13, fontFamily: PP,
    outline: 'none', boxSizing: 'border-box', resize: 'vertical', color: C.gris, background: '#fff',
  }} />;
}

function Th({ children }) {
  return <th style={{ padding: '12px 14px', fontSize: 11, fontWeight: 700, color: C.grisClaro, textAlign: 'left', textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>{children}</th>;
}

function Td({ children, bold, small }) {
  return <td style={{
    padding: '12px 14px', fontSize: small ? 11 : 13,
    color: bold ? C.gris : C.grisClaro,
    fontWeight: bold ? 600 : 400,
    whiteSpace: 'nowrap',
  }}>{children}</td>;
}
